import { Box, Divider, Typography, Grid, Button } from '@mui/material'
import { formatOrdinals } from '../../../utils/Common'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { ReactNode, useCallback, useMemo } from 'react'
import { deselectAreas, selectAreas } from '../../../actions/takeOff'
import { Storey } from '../../../models/Construction'
import { createSelector } from '@reduxjs/toolkit'

const selectSelectedAreasIds = createSelector(
  (state: RootState) => state.takeOff.selectedAreasIds,
  (selectedAreasIds) => new Set(selectedAreasIds),
)

interface ItemButtonProps {
  minHeight: number
  isSelected: boolean
  onClick: () => void
  children: ReactNode
  disabled?: boolean
}

const ItemButton = ({
  minHeight,
  onClick,
  isSelected,
  children,
  disabled,
}: ItemButtonProps) => {
  return (
    <Button
      fullWidth
      disabled={disabled}
      variant='contained'
      onClick={onClick}
      disableElevation
      sx={{
        height: '100%',
        minHeight: `${minHeight}px`,
        borderRadius: '4px',
        background: isSelected ? '#1705E5' : 'white',
        color: isSelected ? 'white' : '#04002E',
        textAlign: 'center',

        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Button>
  )
}

const selectRoomsAndRoofsIds = createSelector(
  [
    (state: RootState) => state.construction.convertData.building.storeys,
    (state: RootState) => state.construction.convertData.building.roofs,
  ],
  (storeys, roofs) => [
    ...storeys.flatMap((storey) => storey.rooms.map((room) => room.id)),
    ...roofs.map((roof) => roof.id),
  ],
)

const EntireHomeButton = () => {
  const dispatch = useDispatch<any>()
  const areasIds = useSelector(selectRoomsAndRoofsIds)
  const selectedAreasIds = useSelector(selectSelectedAreasIds)

  const isSelected = useMemo(() => {
    return (
      areasIds.length > 0 &&
      areasIds.length === selectedAreasIds.size &&
      areasIds.every((id) => selectedAreasIds.has(id))
    )
  }, [areasIds, selectedAreasIds])

  const handleClick = useCallback(() => {
    if (isSelected) {
      dispatch(deselectAreas({ areasIds }))
    } else {
      dispatch(selectAreas({ areasIds }))
    }
  }, [isSelected, areasIds, dispatch])

  return (
    <Grid size={12}>
      <ItemButton isSelected={isSelected} minHeight={42} onClick={handleClick}>
        Entire Home
      </ItemButton>
    </Grid>
  )
}

const StoreyButtons = () => {
  const dispatch = useDispatch<any>()
  const storeys = useSelector(
    (state: RootState) => state.construction.convertData.building.storeys,
  )
  const selectedAreasIds = useSelector(selectSelectedAreasIds)

  const isSelected = useCallback(
    (storey: Storey) => {
      return (
        storey.rooms.length > 0 &&
        storey.rooms.every((room) => selectedAreasIds.has(room.id))
      )
    },
    [selectedAreasIds],
  )

  const handleClick = useCallback(
    (storey: Storey) => {
      const storeyRoomIds = storey.rooms.map((room) => room.id)
      if (isSelected(storey)) {
        dispatch(deselectAreas({ areasIds: storeyRoomIds }))
      } else {
        dispatch(selectAreas({ areasIds: storeyRoomIds }))
      }
    },
    [isSelected, dispatch],
  )

  return (
    <>
      {storeys.map((storey, index) => (
        <Grid key={storey.id} size={12}>
          <ItemButton
            isSelected={isSelected(storey)}
            minHeight={42}
            onClick={() => handleClick(storey)}
          >
            {formatOrdinals(index + 1)} Floor
          </ItemButton>
        </Grid>
      ))}
    </>
  )
}

const RoofButton = () => {
  const dispatch = useDispatch<any>()
  const roofs = useSelector(
    (state: RootState) => state.construction.convertData.building.roofs,
  )
  const selectedAreasIds = useSelector(selectSelectedAreasIds)

  const isSelected = useMemo(() => {
    return (
      roofs.length > 0 && roofs.every((roof) => selectedAreasIds.has(roof.id))
    )
  }, [roofs, selectedAreasIds])

  const handleClick = useCallback(() => {
    const roofsIds = roofs.map((roof) => roof.id)
    if (isSelected) {
      dispatch(deselectAreas({ areasIds: roofsIds }))
    } else {
      dispatch(selectAreas({ areasIds: roofsIds }))
    }
  }, [isSelected, dispatch])

  return (
    <Grid size={12}>
      <ItemButton isSelected={isSelected} minHeight={42} onClick={handleClick}>
        Roof
      </ItemButton>
    </Grid>
  )
}

const selectRoomsFromStoreys = createSelector(
  (state: RootState) => state.construction.convertData.building.storeys,
  (storeys) => storeys.flatMap((storey) => storey.rooms),
)

const RoomButtons = () => {
  const dispatch = useDispatch<any>()
  const rooms = useSelector(selectRoomsFromStoreys)
  const selectedAreasIds = useSelector(selectSelectedAreasIds)

  const isSelected = useCallback(
    (roomId: string) => {
      return selectedAreasIds.has(roomId)
    },
    [selectedAreasIds],
  )

  const handleClick = useCallback(
    (roomId: string) => {
      if (isSelected(roomId)) {
        dispatch(deselectAreas({ areasIds: [roomId] }))
      } else {
        dispatch(selectAreas({ areasIds: [roomId] }))
      }
    },
    [isSelected, dispatch],
  )

  return (
    <>
      {rooms.map((room) => (
        <Grid key={room.id} size={6}>
          <ItemButton
            disabled
            isSelected={isSelected(room.id)}
            minHeight={32}
            onClick={() => handleClick(room.id)}
          >
            {room.name}
          </ItemButton>
        </Grid>
      ))}
    </>
  )
}

const AreaQuickSelect = () => {
  return (
    <Box
      sx={{
        gap: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          color: '#000',

          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
        }}
      >
        Area Quick Select
      </Typography>

      <Box
        sx={{
          flex: '1 1 0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            width: '16px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#BBBCBC',
            backgroundClip: 'padding-box',
            border: '6px solid transparent',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Grid container spacing={1}>
          <EntireHomeButton />

          <StoreyButtons />

          <RoofButton />
        </Grid>
      </Box>

      <Divider
        flexItem
        variant='fullWidth'
        sx={{
          backgroundColor: '#E1E1E1',
          marginRight: '16px',
        }}
      />

      <Box
        sx={{
          flex: '2 1 0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            width: '16px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            backgroundColor: '#BBBCBC',
            backgroundClip: 'padding-box',
            border: '6px solid transparent',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Grid container spacing={1}>
          <RoomButtons />
        </Grid>
      </Box>
    </Box>
  )
}

export default AreaQuickSelect
